import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

/*
 * `text-base lg:text-sm`: abaixo de 16px o Safari do iPhone dá zoom sozinho ao
 * focar o campo e a página fica torta. 16px no celular e no tablet resolve; do
 * `lg` para cima volta aos 14px do layout de referência.
 */
const controlBase =
  'w-full rounded border bg-surface px-3 text-base lg:text-sm text-ink placeholder:text-ink-faint ' +
  'transition-colors disabled:bg-surface-muted disabled:text-ink-faint'

const controlTone = (invalid?: boolean) =>
  invalid
    ? 'border-danger focus:border-danger'
    : 'border-line-strong hover:border-ink-faint focus:border-brand'

interface FieldShellProps {
  label?: ReactNode
  /** Renderizado à direita do rótulo, ex.: link "Esqueceu a senha?". */
  labelAside?: ReactNode
  hint?: ReactNode
  error?: string | null
  htmlFor: string
  children: ReactNode
  className?: string
}

function FieldShell({
  label,
  labelAside,
  hint,
  error,
  htmlFor,
  children,
  className,
}: FieldShellProps) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {(label || labelAside) && (
        <div className="flex items-baseline justify-between gap-3">
          {label && (
            <label htmlFor={htmlFor} className="text-sm text-ink">
              {label}
            </label>
          )}
          {labelAside}
        </div>
      )}
      {error && (
        <p className="text-[13px] font-medium text-danger" id={`${htmlFor}-error`}>
          {error}
        </p>
      )}
      {children}
      {hint && <p className="text-xs leading-snug text-ink-faint">{hint}</p>}
    </div>
  )
}

/* ------------------------------------------------------------------ */

interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label?: ReactNode
  labelAside?: ReactNode
  hint?: ReactNode
  error?: string | null
  className?: string
  inputClassName?: string
}

export function TextField({
  label,
  labelAside,
  hint,
  error,
  className,
  inputClassName,
  id,
  ...props
}: TextFieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId

  return (
    <FieldShell
      label={label}
      labelAside={labelAside}
      hint={hint}
      error={error}
      htmlFor={fieldId}
      className={className}
    >
      <input
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        className={cn(controlBase, controlTone(!!error), 'h-11', inputClassName)}
        {...props}
      />
    </FieldShell>
  )
}

/* ------------------------------------------------------------------ */

interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | null
  className?: string
}

export function SelectField({
  label,
  hint,
  error,
  className,
  id,
  children,
  ...props
}: SelectFieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      htmlFor={fieldId}
      className={className}
    >
      <select
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        className={cn(controlBase, controlTone(!!error), 'h-11 pr-8')}
        {...props}
      >
        {children}
      </select>
    </FieldShell>
  )
}

/* ------------------------------------------------------------------ */

interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label?: ReactNode
  hint?: ReactNode
  error?: string | null
  className?: string
}

export function TextAreaField({
  label,
  hint,
  error,
  className,
  id,
  rows = 6,
  ...props
}: TextAreaFieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      htmlFor={fieldId}
      className={className}
    >
      <textarea
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        className={cn(controlBase, controlTone(!!error), 'resize-y py-2.5 leading-relaxed')}
        {...props}
      />
    </FieldShell>
  )
}
